/**
 * Filter bar for the customer list.
 *
 * Every control writes to the URL rather than to local state, so a filtered
 * view is linkable — "the overdue accounts in Iberia" is a thing one person
 * sends another.
 *
 * `overdue` is a toggle for the same reason it is on the pilot list: it answers
 * the question the list exists for, and a three-state dropdown would bury a
 * yes/no behind two clicks.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.3, §7.3
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RELATIONSHIP_STAGES } from '../../../components/domain/relationship-stages';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../../components/ui/Dialog';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { filterHandler } from '../../../lib/filter-handler';

export interface CustomerFiltersProps {
  filters: Record<string, string>;
  onFilterChange: (key: string, value: string) => void;
  onClear: () => void;
}

/** The controls, shared by the inline bar and the sheet. */
function FilterControls({
  filters,
  onFilterChange,
}: Omit<CustomerFiltersProps, 'onClear'>): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <>
      <label className="flex-1">
        <span className="sr-only">{t('customer.filters.search')}</span>
        <Input
          type="search"
          value={filters['q'] ?? ''}
          onChange={(event) => {
            onFilterChange('q', event.target.value);
          }}
          placeholder={t('customer.filters.searchPlaceholder')}
        />
      </label>

      <Select
        value={filters['relationshipStage']}
        onValueChange={filterHandler('relationshipStage', onFilterChange)}
        options={RELATIONSHIP_STAGES.map((stage) => ({
          value: stage,
          label: t(`customer.stage.${stage}`),
        }))}
        placeholder={t('customer.filters.anyStage')}
        ariaLabel={t('customer.filters.stage')}
        className="sm:w-56"
      />

      {/* `aria-pressed` rather than a checkbox: it changes what the list shows
          immediately, not a value to be submitted. */}
      <Button
        variant={filters['overdue'] === 'true' ? 'primary' : 'secondary'}
        size="sm"
        aria-pressed={filters['overdue'] === 'true'}
        onClick={() => {
          /* Cleared by removing the key, not by storing `"false"` — the URL is
             the state, and `?overdue=false` reads as an applied filter. */
          onFilterChange('overdue', filters['overdue'] === 'true' ? '' : 'true');
        }}
      >
        {t('customer.filters.overdue')}
      </Button>
    </>
  );
}

export function CustomerFilters({
  filters,
  onFilterChange,
  onClear,
}: CustomerFiltersProps): React.JSX.Element {
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);

  const appliedCount = Object.keys(filters).length;

  return (
    <>
      {/* Inline bar, 768px and up. */}
      <div className="gap-s2 hidden items-center md:flex">
        <FilterControls filters={filters} onFilterChange={onFilterChange} />
        {appliedCount > 0 ? (
          <Button variant="ghost" size="sm" onClick={onClear}>
            {t('customer.filters.clear')}
          </Button>
        ) : null}
      </div>

      {/* Below 768px: one button opening a sheet, with the applied count on it
          so active filters are visible without opening it. */}
      <div className="md:hidden">
        <Button
          variant="secondary"
          onClick={() => {
            setSheetOpen(true);
          }}
          className="w-full"
        >
          <Icon name="search" />
          {appliedCount === 0
            ? t('actions.filter')
            : t('customer.filters.applied', { count: appliedCount })}
        </Button>

        <Dialog
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          title={t('actions.filter')}
          footer={
            <>
              <Button variant="ghost" onClick={onClear}>
                {t('customer.filters.clear')}
              </Button>
              <Button
                onClick={() => {
                  setSheetOpen(false);
                }}
              >
                {t('actions.filter')}
              </Button>
            </>
          }
        >
          <div className="gap-s3 flex flex-col">
            <FilterControls filters={filters} onFilterChange={onFilterChange} />
          </div>
        </Dialog>
      </div>
    </>
  );
}
